import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Button,
    CircularProgress,
    TextField
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { getTotalRecordsService } from "../../../../utils/services/audio.service";
import { CheckStorage, StorageData } from "../../../../utils/services/storage.service";
import Divider from '@mui/material/Divider';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'



const PeopleImage = '/images/agent (1).jpeg'; // Path relative to the public folder

interface ApiResponse {
    total_records: number;
    total_agents: number;
    total_audio_duration: string;
    total_audio_min: string;
    average_duration: number;
    top_performance: string;
    low_performance: string;
}

const Dashboard: React.FC = () => {
    const [totalCalls, setTotalCalls] = useState<string>("0");
    const [totalAgents, setTotalAgents] = useState<string>("0");
    const [totalDuration, setTotalDuration] = useState<string>("0");
    const [averageDuration, setAverageDuration] = useState<string>("0");
    const [totalNumberLoading, setTotalNumberLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [topPerformers, setTopPerformers] = useState<any[]>([]);
    const [lowPerformers, setLowPerformers] = useState<any[]>([]);

    const fetchTotalNumbers = async (startDate: string = '', endDate: string = '') => {
        setTotalNumberLoading(true);
        setError(null);

        try {
            const result = await getTotalRecordsService(startDate, endDate) as ApiResponse;
            console.log(result);

            if (result) {
                setTotalCalls(result.total_records.toString());
                setTotalAgents(result.total_agents.toString());
                setTotalDuration(result.total_audio_min ? result.total_audio_min.toString() : '0');
                setAverageDuration(result.average_duration ? result.average_duration.toString() : '0');
                
                setTopPerformers(Array.isArray(result.top_performance) ? result.top_performance : []);
                setLowPerformers(Array.isArray(result.low_performance) ? result.low_performance : []);
            } else {
                throw new Error("No data received");
            }
        } catch (err) {
            console.error("Error fetching total records:", err);
        } finally {
            setTotalNumberLoading(false);
        }
    };

    useEffect(() => {
        StorageData();
        fetchTotalNumbers();
    }, []);

    const handleFilter = () => {
        if (!startDate && !endDate) {
            fetchTotalNumbers();
            setErrorMessage("");
        } else if (startDate && !endDate) {
            setErrorMessage("Please select an end date when a start date is selected.");
        } else if (startDate > endDate) {
            setErrorMessage("Start date cannot be after end date.");
        } else {
            fetchTotalNumbers(startDate, endDate);
            setErrorMessage("");
            setStartDate("");
            setEndDate("");
        }
    };

    return (
        <Box sx={{ padding: 4}}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center", // Ensures vertical alignment
                    mb: 2, // Adds margin below the row
                    fontWeight:"bold",
                }}
            >
                {/* Dashboard Title */}
                <Typography variant="h1" gutterBottom fontWeight="bold">
 
                    Dashboard
                </Typography>
    
                {/* Filters */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginRight: 2 }}
                    />
                <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ marginRight: 2 }}
                />
                <Button variant="contained" onClick={handleFilter} disabled={totalNumberLoading}>
                    Apply Filter
                </Button>
            </Box>
            </Box>

            {error && <Typography color="error">{error}</Typography>}

            <Grid container spacing={2}>
    {/* Total Calls */}
    <Grid item xs={12} md={3}>
        <Card
            sx={{
                // background: "linear-gradient(45deg,#FF5370,#ff869a)", // Pink gradient
                background:"#FFFFFF",
                borderRadius: "16px",
                padding: 2,
                width: "90%",
                boxShadow: "0 6px 9px rgba(0, 0, 0, 0.4)",
                // transition: "transform 0.3s, background-color 0.3s",
                // "&:hover": {
                //     backgroundColor: "#4099ff",
                //     transform: "scale(1.05)",
                // },
            }}
        >
            <CardHeader
                title={
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            color: "black",
                            fontSize: "1.5rem",
                            // fontWeight: "bold",
                        }}
                    >
                        <CallIcon sx={{ marginRight: 1, color: "#3498db",fontSize: "40px"}} />
                        Total Calls
                    </Box>
                }
            />
            <CardContent>
                {totalNumberLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center"  height="150px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Typography color="black" variant="h1" component="h1" fontWeight= "bold"  >
                        {totalCalls}
                    </Typography>
                )}
            </CardContent>
        </Card>
    </Grid>

    {/* Total Agents */}
    <Grid item xs={12} md={3}>
        <Card
            sx={{
                // background: "linear-gradient(45deg,#2ed8b6,#59e0c5)", // Green gradient
                background:"#fff",
                borderRadius: "16px",
                padding: 2,
                width: "90%",
                boxShadow: "0 6px 9px rgba(0, 0, 0, 0.4)",
                // transition: "transform 0.3s, background-color 0.3s",
                // "&:hover": {
                //     backgroundColor: "#2ed8b6",
                //     transform: "scale(1.05)",
                // },
            }}
        >
            <CardHeader
                title={
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            color: "black",
                            fontSize: "1.5rem",
                            // fontWeight: "bold",
                        }}
                        
                    >
                     <PeopleIcon sx={{ marginRight: 1, color: "#3498db",fontSize: "40px" // Increase icon siz
                     }} />

                        Total Agents
                    </Box>
                }
            />
            <CardContent>
                {totalNumberLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress />
                    </Box>
                ) : (
                    <Typography color="black" variant="h1" component="h1"fontWeight= "bold">
                        {totalAgents}
                    </Typography>
                )}
            </CardContent>
        </Card>
    </Grid>

    {/* Total Minutes */}
    <Grid item xs={12} md={3}>
        <Card
            sx={{
                // background: "linear-gradient(45deg,#FFB64D,#ffcb80)", // Yellow gradient
                background:"FFFFFF", //
                borderRadius: "16px",
                padding: 2,
                width: "90%",
                boxShadow: "0 6px 9px rgba(0, 0, 0, 0.4)",
                // transition: "transform 0.3s, background-color 0.3s",
                // "&:hover": {
                //     backgroundColor: "#FFB64D",
                //     transform: "scale(1.05)",
                // },
            }}
        >
            <CardHeader
                title={
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            color: "black",
                            fontSize: "1.5rem",
                            // fontWeight: "bold",
                        }}
                    >
                     <AccessTimeIcon sx={{ marginRight: 1, color: "#3498db",fontSize: "40px" }} />


                        Total Minutes
                    </Box>
                }
            />
            <CardContent>
                {totalNumberLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Typography color="black" variant="h1" component="h1"fontWeight= "bold">
                        {totalDuration}
                    </Typography>
                )}
            </CardContent>
        </Card>
    </Grid>

    {/* Average Duration */}
    <Grid item xs={12} md={3}>
        <Card
            sx={{
                // background: "linear-gradient(45deg,#4099ff,#73b4ff)", // Blue gradient
                background: "#fff",

                borderRadius: "16px",
                padding: 2,
                width: "90%",
                boxShadow: "0 6px 9px rgba(0, 0, 0, 0.4)",
                // transition: "transform 0.3s, background-color 0.3s",
                // "&:hover": {
                //     backgroundColor: "#FF5370",
                //     transform: "scale(1.05)",
                // },
            }}
        >
            <CardHeader
                title={
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            color: "back",
                            fontSize: "1.5rem",
                            // fontWeight: "bold",
                        }}
                    >
                     <AccessTimeIcon sx={{ marginRight: 1, color: "#3498db",fontSize: "40px" }} />
                     Average Duration
                    </Box>
                }
            />
            <CardContent>
                {totalNumberLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Typography color="black" variant="h1" component="h1"fontWeight= "bold">
                        {averageDuration}
                    </Typography>
                )}
            </CardContent>
        </Card>
    </Grid>
</Grid>

<Grid container spacing={3} sx={{ marginTop: 4 }}>
        {/* Top Performers Graph */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: 11 }}>
            <CardHeader
              title="Top Performers"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: '#000000'  }}
              sx={{ textAlign: 'center', backgroundColor: '#fff',fontSize: "1.5rem",}}
            />
            <Divider />
            <CardContent>
              {topPerformers.length > 0 ? (
                <ResponsiveContainer width="90%" height={200}>
                  <BarChart data={topPerformers}>
                    <XAxis dataKey="agent_name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="score" fill="#4CAF50" barSize={45} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography align="center" sx={{ color: 'text.secondary', mt: 2 }}>
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Low Performers Graph */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#FFFFFF', boxShadow: 11 }}>
            <CardHeader
              title="Low Performers"
              titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: '#000000' }}
              sx={{ textAlign: 'center', backgroundColor: '#fff' }}
            />
            <Divider />
            <CardContent>
              {lowPerformers.length > 0 ? (
                <ResponsiveContainer width="90%" height={200}>
                  <BarChart data={lowPerformers}>
                    <XAxis dataKey="agent_name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="score" fill="#F44336" barSize={45} /> {/* Adjusted barSize */}
                    </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography align="center" sx={{ color: 'text.secondary', mt: 2 }}>
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Dashboard;







